import { SettingsService } from "./SettingsService";
import { CalcRowConfig, CalcRowsLogic, CalculationRowCategories, CalculationRowSource, CalculationRowType, RowTypeDefinition, UserDefinedRow, Years } from "@salaxy/core";
import { SessionService } from "./SessionService";

/**
 * Provides access to the row types including the company specific custom row types.
 */
export class RowTypeService {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["SettingsService", "SessionService"];

  /** A key that signals when the settings were last updated => Used by downstream caches to refresh.  */
  public timestamp: string;


  /** Cached version of the CalcRowsLogic */
  private _logic?: CalcRowsLogic;

  /**
   * Creates a new instance of RowTypeService
   */
  constructor(private settingsService: SettingsService, private session: SessionService) {
  }

  /**
   * Gets a cached version of CalcRowsLogic with current settings (if available).
   */
  public get logic(): CalcRowsLogic {
    if (this.session.isInRole("household")) {
      if (!this._logic) {
        this.refresh();
        this._logic = new CalcRowsLogic("household");
      }
      return this._logic;
    }
    if (!this.isReady) {
      // Just provide dynamic, should not neede to be cached.
      return new CalcRowsLogic("company");
    }
    if (!this._logic) {
      this.refresh();
      this._logic = new CalcRowsLogic(this.settingsService.current);
    }
    return this._logic;
  }

  /** Clears the cache and reloads the row types (from settings service singleton, not all the way from the server). */
  public refresh(): void {
    this._logic = null;
    this.timestamp = new Date().toLocaleTimeString();
  }

  /**
   * If true, the custom row types (currently in settings) have been loaded from the server.
   */
  public get isReady(): boolean {
    return !!this.settingsService.current;
  }

  /**
   * Gets a filtered list of row types configs
   * @param group If specified, filters the configs by group (builtIn, custom).
   */
  public getConfigs(group?: "builtIn" | "custom", categories?: CalculationRowCategories[]): CalcRowConfig[] {
    let result = this.configs;
    if (group) {
      result = result.filter((x) => x.group === group);
    }
    if (categories && categories.length > 0) {
      result = result.filter((x) => categories.indexOf(x.category) >= 0);
    }

    const obsolete: string[] = [
      CalculationRowType.PaidSickLeaveSalary,
      CalculationRowType.PaidSickLeaveHourlySalary,
      CalculationRowType.PaidSickLeaveMonthlySalary,
      CalculationRowType.TrainingSalary,
      CalculationRowType.TrainingHourlySalary,
      CalculationRowType.TrainingMonthlySalary,
      CalculationRowType.ServiceCharge
    ];
    result = result.filter((x) => !(obsolete.indexOf(x.name) >= 0));

    // For pro and admin only
    if (!this.session.isInSomeRole("pro,admin")) {
      result = result.filter((x) => x.name != CalculationRowType.Service);
    }

    // For bizconfigs only
    if (!this.session.isInRole("bizConfigs")) {
      result = result.filter((x) => x.name != CalculationRowType.Script && x.name != CalculationRowType.PayStats);
    }

    return result;
  }

  private listCache: {
    /** Cache key */
    key: string,
    /** Cache value */
    value: CalcRowConfig[],
  } = null;

  /** Gets a cached list of all configs (even the ones that should typically be hidden from the list by role or because they are obsolete). */
  public get configs(): CalcRowConfig[] {
    if (!this.listCache || this.listCache.key !== this.timestamp) {
      const value = this.logic.getRowConfigs();
      this.listCache = { key: this.timestamp, value };
    }
    return this.listCache.value;
  }

  /** Gets the editble rows collection. */
  public get allCustomRows(): RowTypeDefinition[] {
    return this.settingsService.current?.calc.salaryDefaults as RowTypeDefinition[] ?? [];
  }

  /**
   * Gets the configuration for a row: May be built-in or custom row type..
   * @param row Row for which to get the configuration.
   * @returns A configuration if found, otherwise undefined.
   */
  public getConfig(row: UserDefinedRow): CalcRowConfig | undefined {
    if (!row) {
      return undefined;
    }
    if (row.source == CalculationRowSource.CustomRowtype) {
      return (row as RowTypeDefinition).config as CalcRowConfig;
    }
    let result: CalcRowConfig;
    if (row.type?.startsWith("/")) {
      result = this.logic.getRowConfigs().find((x) => x.type == row.type);
      if (result) {
        return result;
      }
      console.warn(`Unable to find the row based on the new type '${row.type}' => Fall back to rowType based resolve.`);
    }

    result = this.logic.getRowConfig(row.rowType);
    if (!result) {
      console.error(`Row type '${row.rowType}' not found in the row type definitions.`);
    }
    return result;
  }

  /**
   * Deletes an item in the allCustomRows collection.
   * This does not yet save the changes or even refresh the cache.
   */
  public deleteRow(row: RowTypeDefinition): void {
    const index = this.allCustomRows.indexOf(row);
    if (index >= 0) {
      this.allCustomRows.splice(index, 1);
    }
  }

  /** Saves the current, potentitally modified set of custom rows. */
  public async save(): Promise<void> {
    this.settingsService.saveWithoutSessionCheck();
    this.refresh();
  }

  /**
   * Call this method when a new row is created to apply the default values.
   */
  public applyDefaultsToNewRow(row: UserDefinedRow, salaryDate: string): void {
    if (!row || !row.rowType) {
      return;
    }
    const config = this.getConfig(row);
    if (config.amount.default) {
      row.count = config.amount.default;
    }
    if (config.price.default) {
      row.price = config.price.default;
    }

    const specialKnownRowTypes = this.specialKnownRowTypesAtSalaryDate(salaryDate);
    if (specialKnownRowTypes[config.name]) {
      row.price = specialKnownRowTypes[config.name];
    }

    if (config.group == "custom") {
      const customRow = this.allCustomRows.find((x) => x.source == CalculationRowSource.CustomRowtype && x.type == config.type);
      if (customRow) {
        //Set message explicitly because the report rendering does not have access to company specific custom rows (yet).
        if(config.label) {
          row.message = config.label;
        }
        if(customRow.message) {
          row.message = customRow.message;
        }
        if (customRow.count) {
          row.count = customRow.count;
        }
        if (customRow.price) {
          row.price = customRow.price;
        }
        if(customRow.unit) {
          row.unit = customRow.unit;
        }
        if(customRow.accounting) {
          row.accounting = customRow.accounting;
        }
        if(customRow.data) {
          row.data = customRow.data;
        }
      }
    }
  }

  private specialKnownRowTypesAtSalaryDate(salaryDate): { [key: string]: number } {
    const yearlyNumbers = Years.getYearlyChangingNumbers(salaryDate ?? "today");
    return {
      dailyAllowance: yearlyNumbers.sideCosts.taxFreeDailyAllowance,
      dailyAllowanceHalf: yearlyNumbers.sideCosts.taxFreeDailyHalfAllowance,
      mealCompensation: yearlyNumbers.sideCosts.taxFreeMealAllowance,
    };
  }

}